import React, { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Drawer,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  DiscountRuleDto,
  useDeleteDiscountRule,
  useDiscountRules,
  useCreateDiscountRule,
  useFanTiers,
  useRuleRedemptions,
  useSetDiscountRuleActive,
  useUpdateDiscountRule,
} from '@shared/api/hooks/useDiscountRules';
import {
  DEFAULT_RULE_VALUES,
  DiscountRuleForm,
  RuleFormValues,
  fromRule,
  toInput,
} from './DiscountRuleForm';
import { DiscountSimulator } from './DiscountSimulator';
import { FanTiersPanel } from './FanTiersPanel';
import { TierMatrix } from './TierMatrix';
import { describeAction, describeRule } from './ruleSummary';

const { Text } = Typography;

export function DiscountRulesPage(): React.ReactElement {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [redemptionsFor, setRedemptionsFor] = useState<DiscountRuleDto | null>(null);
  const [form] = Form.useForm<RuleFormValues>();

  const { data: rules = [], isLoading, refetch, isFetching } = useDiscountRules();
  const { data: tiers = [] } = useFanTiers();
  const createMutation = useCreateDiscountRule();
  const updateMutation = useUpdateDiscountRule();
  const setActive = useSetDiscountRuleActive();
  const deleteMutation = useDeleteDiscountRule();
  const redemptions = useRuleRedemptions(redemptionsFor?.id ?? null);

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue(DEFAULT_RULE_VALUES);
    setDrawerOpen(true);
  };

  const openEdit = (r: DiscountRuleDto): void => {
    setEditId(r.id);
    form.resetFields();
    form.setFieldsValue(fromRule(r));
    setDrawerOpen(true);
  };

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    const input = toInput(v);
    try {
      if (editId) {
        const { code: _code, trigger: _trigger, ...rest } = input;
        await updateMutation.mutateAsync({ id: editId, input: rest });
        message.success('Regola aggiornata');
      } else {
        await createMutation.mutateAsync(input);
        message.success('Regola creata');
      }
      setDrawerOpen(false);
    } catch (e) {
      // Il backend rifiuta le combinazioni incoerenti con un messaggio parlante:
      // mostriamolo cosi' com'e' invece di un generico "errore".
      const msg =
        (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message ??
        String(e);
      message.error(Array.isArray(msg) ? msg.join(' · ') : msg);
    }
  };

  const rulesTab = (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nuova regola
        </Button>
        <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => void refetch()}>
          Aggiorna
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={rules}
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (r) => <Text type="secondary">{describeRule(r, tiers)}</Text>,
        }}
        columns={[
          {
            title: 'Priorita',
            dataIndex: 'priority',
            width: 90,
            sorter: (a, b) => a.priority - b.priority,
            defaultSortOrder: 'ascend' as const,
            render: (p: number, r: DiscountRuleDto) => (
              <InputNumber
                size="small"
                min={0}
                max={9999}
                value={p}
                style={{ width: 70 }}
                onChange={(v) => {
                  if (v !== null && v !== p) {
                    void updateMutation.mutateAsync({ id: r.id, input: { priority: v } });
                  }
                }}
              />
            ),
          },
          {
            title: 'Nome',
            dataIndex: 'name',
            render: (name: string, r: DiscountRuleDto) => (
              <Space direction="vertical" size={0}>
                <Text strong>{name}</Text>
                <Space size={4}>
                  <Tag color={r.trigger === 'CODE' ? 'purple' : 'blue'}>
                    {r.trigger === 'CODE' ? `CODICE ${r.code}` : 'AUTO'}
                  </Tag>
                  {!r.stackable && <Tag color="orange">non cumulabile</Tag>}
                  {r.exclusive && <Tag color="red">esclusiva</Tag>}
                </Space>
              </Space>
            ),
          },
          { title: 'Sconto', key: 'action', render: (_, r) => describeAction(r) },
          {
            title: 'Tier',
            key: 'tiers',
            render: (_, r: DiscountRuleDto) =>
              r.tiers.length === 0 ? (
                <Tag color="blue">tutti</Tag>
              ) : (
                <Space size={2} wrap>
                  {r.tiers.map((t) => (
                    <Tag key={t.tierId}>{tiers.find((x) => x.id === t.tierId)?.key ?? '?'}</Tag>
                  ))}
                </Space>
              ),
          },
          {
            title: 'Limiti',
            key: 'limits',
            render: (_, r: DiscountRuleDto) => (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.maxRedemptionsPerUser
                  ? `${r.maxRedemptionsPerUser}/tifoso (${r.perUserPeriod})`
                  : 'illimitato'}
                {r.maxRedemptionsTotal ? ` · tot ${r.maxRedemptionsTotal}` : ''}
              </Text>
            ),
          },
          {
            title: 'Attiva',
            dataIndex: 'isActive',
            width: 80,
            render: (v: boolean, r: DiscountRuleDto) => (
              <Switch
                size="small"
                checked={v}
                loading={setActive.isPending}
                onChange={(checked) => void setActive.mutateAsync({ id: r.id, isActive: checked })}
              />
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 140,
            render: (_, r: DiscountRuleDto) => (
              <Space size={4}>
                <Button
                  size="small"
                  icon={<UnorderedListOutlined />}
                  onClick={() => setRedemptionsFor(r)}
                  title="Utilizzi"
                />
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare la regola?"
                  description="Se e gia stata usata verra solo disattivata, per non rompere lo storico ordini."
                  onConfirm={async () => {
                    const res = await deleteMutation.mutateAsync(r.id);
                    message.success(res.deleted ? 'Regola eliminata' : 'Regola disattivata (aveva utilizzi)');
                  }}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </>
  );

  return (
    <Card
      title="Sconti store"
      extra={<Badge count={rules.filter((r) => r.isActive).length} showZero color="green" />}
    >
      <Tabs
        items={[
          { key: 'rules', label: 'Regole', children: rulesTab },
          { key: 'tiers', label: 'Tier tifoso', children: <FanTiersPanel /> },
          {
            key: 'matrix',
            label: 'Matrice Tier',
            children: <TierMatrix rules={rules} tiers={tiers} />,
          },
          {
            key: 'sim',
            label: (
              <span>
                <ExperimentOutlined /> Simulatore
              </span>
            ),
            children: <DiscountSimulator tiers={tiers} />,
          },
        ]}
      />

      <Drawer
        title={editId ? 'Modifica regola' : 'Nuova regola'}
        width={860}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Annulla</Button>
            <Button
              type="primary"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={() => void onSubmit()}
            >
              Salva
            </Button>
          </Space>
        }
      >
        <DiscountRuleForm form={form} tiers={tiers} isEdit={Boolean(editId)} />
      </Drawer>

      <Modal
        title={`Utilizzi — ${redemptionsFor?.name ?? ''}`}
        open={Boolean(redemptionsFor)}
        onCancel={() => setRedemptionsFor(null)}
        footer={null}
        width={760}
      >
        <Table
          size="small"
          rowKey="id"
          loading={redemptions.isLoading}
          dataSource={redemptions.data?.items ?? []}
          pagination={{ pageSize: 10, total: redemptions.data?.total ?? 0 }}
          columns={[
            {
              title: 'Quando',
              dataIndex: 'createdAt',
              render: (v: string) => new Date(v).toLocaleString('it-IT'),
            },
            { title: 'Tifoso', dataIndex: 'userId', ellipsis: true },
            { title: 'Ordine', dataIndex: 'orderId', ellipsis: true },
            { title: 'Periodo', dataIndex: 'periodKey', width: 100 },
            {
              title: 'Importo',
              dataIndex: 'amount',
              align: 'right' as const,
              render: (v: string) => `${Number(v).toFixed(2)} EUR`,
            },
            {
              title: 'Stato',
              dataIndex: 'voidedAt',
              width: 110,
              render: (v: string | null) =>
                v ? <Tag color="default">restituito</Tag> : <Tag color="green">valido</Tag>,
            },
          ]}
        />
      </Modal>
    </Card>
  );
}
